/* eslint-disable react/jsx-pascal-case */
import { useGetApiV1UnderwritingModelsQuery } from '../../models/hooks';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    IconButton,
    Toolbar,
    CircularProgress,
    Box,
    Stack,
    Snackbar,
    Alert,
} from '@mui/material';
import React, { FC, useState, useEffect, useRef } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import PathsForm from '../components/organisms/PathsForm/PathsForm';
import {
    useDeleteApiV1UnderwritingPathsByPathIdMutation,
    useGetApiV1UnderwritingPathsByPathIdQuery,
    usePostApiV1UnderwritingPathsByPathIdMutation,
    usePostApiV1UnderwritingPathsMutation,
} from '../hooks';
import { Close } from '@mui/icons-material';
import { ModelType } from '../../models/api/api-types';
import { Path, PathFilter, PathModel } from '../api/generated';
import { DuplicateButton } from '../../DuplicateButton';
import { CopyPasteModels } from '../../UnderwritePath/CopyPasteModels';
import { CopyPasteFilters } from '../../UnderwritePath/CopyPasteFilters';
import { hasPathChanges } from '../utils/hasPathChanges';

interface IBaseInternalProps {
    title: string;
    handleClose: () => void;
    // handleDelete?: (pathId: number) => Promise<void>,
    // handleSubmit: (p: Path) => Promise<any> // todo change this
}
interface ISharedProps {}
interface IEditProps extends ISharedProps {
    path: number;
    handleClose: () => void;
    onDuplicateSuccess?: () => void;
}
interface ICreateProps extends ISharedProps {
    path: 'CREATE';
    handleClose: () => void;
}
// interface ITabsComposition {
//   Edit: React.FC<IEditProps>;
//   Create: React.FC<ICreateProps>;
// }

/*------------------*/
/*----- CREATE -----*/
/*------------------*/
const CreatePathsModal: FC<ICreateProps> = ({ handleClose }) => {
    const [createPath, createPathData] = usePostApiV1UnderwritingPathsMutation();

    const handleSubmit = async (v: Path) => {
        await createPath({ ...v });
        handleClose();
    };

    const modelsQuery = useGetApiV1UnderwritingModelsQuery(
        {},
        {
            selectFromResult: (result) => ({
                ...result,
                data: {
                    ...(result.isSuccess
                        ? {
                              allModels:
                                  Object.values(result.data.entities).reduce<
                                      { modelId: number; name: string; modelType: ModelType }[]
                                  >((prev, { ...curr }) => {
                                      return [
                                          ...prev,
                                          {
                                              ...(curr && {
                                                  modelId: curr.modelId,
                                                  name: curr.name,
                                                  modelType: curr.modelType,
                                              }),
                                          },
                                      ];
                                     }, []) ?? [],
                              // Object.values(data.entities).map((model) => model && ({
                              //   modelId: model?.modelId,
                              //   name: model?.name,
                              //   modelType: model?.modelType,
                              // })) ?? [],
                          }
                        : { allModels: [] }),
                },
            }),
        },
    );

    return (
        <_PathsModal title="Create Path" handleClose={handleClose}>
            <DialogContent>
                <DialogContentText>
                    {modelsQuery.isSuccess ? (
                        <PathsForm
                            models={modelsQuery.data.allModels}
                            submitLoading={createPathData.isLoading}
                            onSubmit={handleSubmit}
                        />
                    ) : (
                         <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Box>
                    )}
                </DialogContentText>
            </DialogContent>
        </_PathsModal>
    );
};

/*----------------*/
/*----- EDIT -----*/
/*----------------*/
const EditPathsModal: FC<IEditProps> = ({ path, handleClose,onDuplicateSuccess }) => {
    // make the title
    const [localModels, setLocalModels] = useState<PathModel[]>([]);

    const [localFilters, setLocalFilters] = useState<PathFilter[]>([]);

    const [deletePath, deletePathData] = useDeleteApiV1UnderwritingPathsByPathIdMutation();

    const [editPath, editPathData] = usePostApiV1UnderwritingPathsByPathIdMutation({});

    // const pathsQuery = useGetApiV1UnderwritingPathsByPathIdQuery({ pathId: path });
    const pathsQuery = useGetApiV1UnderwritingPathsByPathIdQuery({ pathId: path }, { refetchOnMountOrArgChange: false });

    useEffect(() => {
    const handleGlobalPaste = (e: Event) => {
        const clipboardEvent = e as ClipboardEvent;

        const text = clipboardEvent.clipboardData?.getData('text') ?? '';
        if (!text) return;

        let data: any;
        try {
            data = JSON.parse(text);
        } catch {
            return;
        }

        if (data?.type !== 'PATH_MODELS' && data?.type !== 'PATH_FILTERS') {
            return;
        }

        e.preventDefault();
        e.stopPropagation();

        if (data.type === 'PATH_MODELS') {
            setLocalModels(Array.isArray(data.models) ? data.models : []);
        }

        if (data.type === 'PATH_FILTERS') {
            setLocalFilters(Array.isArray(data.filters) ? data.filters : []);
        }
    };

    window.addEventListener('paste', handleGlobalPaste as EventListener);
    return () => window.removeEventListener('paste', handleGlobalPaste as EventListener);
}, []);

    useEffect(() => {
        if (pathsQuery.isSuccess && pathsQuery.data) {

            setLocalModels(pathsQuery.data.pathModels || []);

            setLocalFilters(pathsQuery.data.pathFilterModels || []);
        }
    }, [pathsQuery.isSuccess, pathsQuery.data]);

    const originalPathRef = useRef<Path | null>(null);
    useEffect(() => {
        if (pathsQuery.isSuccess && pathsQuery.data) {
            originalPathRef.current = cloneDeep(pathsQuery.data);
        }
    }, [pathsQuery.isSuccess, pathsQuery.data]);

    const [noChangesWarningOpen, setNoChangesWarningOpen] = useState(false);

    const handleSubmit = async (v: Path) => {
        const originalPath = originalPathRef.current;
        if (originalPath && !hasPathChanges(originalPath, v)) {
            setNoChangesWarningOpen(true);
            return;
        }
        await editPath({ path: { ...v, pathId: path }, pathId: path });
        handleClose();
    };
    const handleDelete = async (id: number) => {
        await deletePath(id);
        // feedback?
        handleClose();
    };
    const modelsQuery = useGetApiV1UnderwritingModelsQuery(
        {},
        {
            selectFromResult: (result) => ({
                ...result,
                data: {
                    ...(result.isSuccess
                        ? {
                              allModels:
                                  Object.values(result.data.entities).reduce<
                                      { modelId: number; name: string; modelType: ModelType }[]
                                  >((prev, { ...curr }) => {
                                      return [
                                          ...prev,
                                          {
                                              ...(curr && {
                                                  modelId: curr.modelId,
                                                  name: curr.name,
                                                  modelType: curr.modelType,
                                              }),
                                          },
                                      ];
                                  }, []) ?? [],
                              // Object.values(data.entities).map((model) => model && ({
                              //   modelId: model?.modelId,
                              //   name: model?.name,
                              //   modelType: model?.modelType,
                              // })) ?? [],
                          }
                        : { allModels: [] }),
                },
            }),
        },
    );

    return (
        <_PathsModal title={`Edit Path: ${path}`} handleClose={handleClose}>
            <DialogContent>
                {pathsQuery.isSuccess && pathsQuery.data && (
                    <Stack
                        direction="column"
                        spacing={2}
                        sx={{
                            mb: 3,
                            width: '100%',
                        }}
                    >
                        <Stack direction="row" spacing={2}>
                            <DuplicateButton path={pathsQuery.data} refetch={pathsQuery.refetch} onSuccess={() => {     
                                    handleClose();
                                    onDuplicateSuccess?.();
                                }} />
                            <CopyPasteModels models={localModels} setModels={setLocalModels} />
                            <CopyPasteFilters filters={localFilters} setFilters={setLocalFilters} />
                        </Stack>
                    </Stack>
                )}
                {modelsQuery.isSuccess && pathsQuery.isSuccess && !pathsQuery.isFetching ? (
                    <PathsForm
                        onSubmit={handleSubmit}
                        submitLoading={editPathData.isLoading}
                        onDelete={handleDelete}
                        delLoading={deletePathData.isLoading} //this should honestly be moved up back to PathsViewModal so we don't have to pass these props but not worrying about it for now.
                        models={modelsQuery.data.allModels}
                        path={pathsQuery.data}
                        copymodels={localModels}
                        filters={localFilters}
                    />
                ) : (
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                        <CircularProgress />
                    </Stack>
                )}
            </DialogContent>
            <Snackbar
                open={noChangesWarningOpen}
                autoHideDuration={4000}
                onClose={() => setNoChangesWarningOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity="warning" onClose={() => setNoChangesWarningOpen(false)} variant="filled">
                    No changes to update
                </Alert>
            </Snackbar>
        </_PathsModal>
    );
};

// todo, make this a reusable organism? technically is now
const _PathsModal: React.FC<IBaseInternalProps> = ({ title, children, ...props }) => {
    // compound component pattern? This could be context instead of prop drilling
        const [open, setOpen] = React.useState(true);
    function handleClose(e: unknown, r: string) {
        if (r === 'escapeKeyDown') {
            setOpen(false);
            props.handleClose();
        }
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionProps={{ unmountOnExit: true }}
            maxWidth={'lg'}
            PaperProps={{ style: { overflowY: 'hidden' } }}
        >
            <Toolbar sx={{ justifyContent: 'space-between' }}>
                <DialogTitle>{title}</DialogTitle>
                <IconButton
                    color={'inherit'}
                    onClick={(e) => handleClose && handleClose(e, 'escapeKeyDown')}
                    size="large"
                >
                    <Close />
                </IconButton>
            </Toolbar>
            {children}
        </Dialog>
    );
};
export const PathsModal: {
    Edit: React.FC<IEditProps>;
    Create: React.FC<ICreateProps>;
} = {
    Edit: EditPathsModal,
    Create: CreatePathsModal,
};

export { PathsModal as Tabs };
